import { useRouter } from "next/router";

import { apiPath } from "shared/helpers/apiPath";
import useRequest from "shared/helpers/useRequest";
import Custom404 from "src/pages/404";
import client, { apiClient, apiClientV2 } from "@/helpers/apiClient";
import PageWrapper from "@/components/PageWrapper";
import Container from "@/components/Container";
import { GridColumns } from "@/components/Container/Container.interface";
import Typography from "@/components/Typography";
import SkeletonLoader from "@/components/SkeletonLoader";
import { TagPageInterface } from "@/helpers/pageHelpers/Tag/interface";
import MetaHead from "@/components/MetaHead";
import TagList from "@/containers/Feed/Tag/TagList";

import styles from "./Tag.module.scss";

export const Tag: React.FC<TagPageInterface> = ({ initialData }) => {
  const router = useRouter();
  const { slug } = router.query;
  const { data: tagData } = useRequest(apiPath.TAG(slug), { initialData });

  if (router.isFallback) {
    return (
      <PageWrapper>
        <div className={styles.top}>
          <Container>
            <div>
              <Typography
                variant="heading"
                component="h1"
                className={styles.title}
              >
                <SkeletonLoader height={50} />
              </Typography>
            </div>
          </Container>
        </div>
        <Container grid={GridColumns.Three}>
          <div className={styles.feedTiles}>
            <SkeletonLoader />
            <SkeletonLoader />
            <SkeletonLoader />
          </div>
        </Container>
      </PageWrapper>
    );
  }

  if (!tagData) {
    return <Custom404 />;
  }

  const { title, headMetaData } = tagData;
  const pageData = { headerLabel: title };

  return (
    <PageWrapper pageData={pageData}>
      <MetaHead metaData={headMetaData} />
      <div className={styles.top}>
        <Container>
          <div>
            <Typography
              variant="heading"
              component="h1"
              className={styles.title}
            >
              {title}
            </Typography>
          </div>
        </Container>
      </div>
      <TagList query={slug} initialData={initialData} />
    </PageWrapper>
  );
};

export async function getStaticPaths() {
  const client = apiClient();

  const { data: tags } = await client.get(apiPath.MAIN_TAGS);

  const paths =
    tags &&
    tags.map((tag) => ({
      params: { slug: tag.slug },
    }));

  return {
    paths,
    fallback: true,
  };
}

export async function getStaticProps(context) {
  const clientV2 = apiClientV2();
  const { params } = context;

  try {
    const { data } = await client.get(apiPath.TAG(params.slug));
    const { data: feed } = await clientV2.get(
      apiPath.FEED_TAGS({ slug: params.slug, page: 1 })
    );

    return {
      props: {
        initialData: {
          ...data,
          feed: feed.items,
        },
      },
      revalidate: 86400,
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
}

export default Tag;
